/* eslint-disable @nrwl/nx/workspace/killSubscriptionRule */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

/**
 * An interceptor that limits the maximum number of concurrent requests for the same URL.
 * If the number of concurrent requests for the same URL exceeds the limit, subsequent requests
 * will be queued until one of the active requests completes.
 */
@Injectable()
export class ConcurrentRequestsInterceptorService implements HttpInterceptor {
	// The maximum number of concurrent requests for the same URL.
	private readonly MAX_CONCURRENT_REQUESTS = 10;

	// A map that stores the active requests for each URL.
	private readonly activeRequests = new Map<string, number>();

	// A queue that stores the queued requests for each URL.
	private readonly queuedRequests = new Map<string, Subject<HttpEvent<any>>[]>();

	public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const url = req.urlWithParams;

		// If the number of active requests for the same URL exceeds the limit,
		// queue the request and return the observable that will emit the response
		// when the request is completed.
		if (this.activeRequests.get(url) >= this.MAX_CONCURRENT_REQUESTS) {
			return this.queueRequest(url).pipe(switchMap(() => next.handle(req)));
		}

		// If the number of active requests for the same URL is below the limit,
		// increment the active request counter and handle the request.
		this.incrementActiveRequests(url);
		return next.handle(req).pipe(
			tap({
				next: (event: HttpEvent<any>) => {
					// If the request is completed successfully, decrement the active
					// request counter and dequeue the next request if any.
					if (event instanceof HttpResponse) {
						this.requestComplete(url);
					}
				},
				complete: () => {
					// If the request is completed with an error, decrement the active
					// request counter and dequeue the next request if any.
					this.requestComplete(url);
				},
				error: (error: HttpErrorResponse) => {
					// Handles HttpErrorResponse
					if (error?.url) {
						this.requestComplete(url);
					}
				}
			})
		);
	}

	/**
	 * When the request is complete
	 */
	private requestComplete(url: string) {
		this.decrementActiveRequests(url);
		this.dequeueRequest(url);
	}

	/**
	 * Queues the request for the given URL and returns the observable that will
	 * emit the response when the request is completed.
	 */
	private queueRequest(url: string): Observable<HttpEvent<any>> {
		let queuedRequests = this.queuedRequests.get(url);
		if (!queuedRequests) {
			queuedRequests = [];
			this.queuedRequests.set(url, queuedRequests);
		}

		const subject = new Subject<HttpEvent<any>>();
		queuedRequests.push(subject);

		return subject.asObservable();
	}

	/**
	 * Dequeues the next request for the given URL if any and emits the response
	 * through the corresponding observable.
	 */
	private dequeueRequest(url: string) {
		const queuedRequests = this.queuedRequests.get(url) || [];
		const subject = queuedRequests.shift();
		if (subject) {
			this.incrementActiveRequests(url);
			subject.subscribe({
				complete: () => {
					this.requestComplete(url);
				}
			});

			subject.next(new HttpResponse());
			subject.complete();
		}
	}

	/**
	 * Increments the active request counter for the given URL.
	 */
	private incrementActiveRequests(url: string) {
		const activeRequests = this.activeRequests.get(url) || 0;
		this.activeRequests.set(url, activeRequests + 1);
	}

	/**
	 * Decrements the active request counter for the given URL.
	*/
	private decrementActiveRequests(url: string) {
		const activeRequests = this.activeRequests.get(url) || 0;
		if (activeRequests > 0) {
			this.activeRequests.set(url, activeRequests - 1);
		}
	}
}
